import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getRequest, postRequest } from 'apis/ApiInstence';
import { generateOrderID } from 'common/CommonFun';
import useCities from 'hooks/useCities';

const initialOrder = {
  customer_id: '',
  name: '',
  phone: '',
  city: '',
  shipping_address: '',
  order_date: new Date().toISOString().slice(0, 10),
  timing: '',
  message: '',
  alternatePhone: '',
  orderType: 'custom',
  latitude: '',
  longitude: '',
};

const CreateCustomOrder = () => {
  const navigate = useNavigate();
  const [order, setOrder] = useState(initialOrder);
  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const { cityOptions } = useCities();

  useEffect(() => {
    const loadFormData = async () => {
      try {
        const [categoryResponse, customerResponse] = await Promise.all([
          getRequest('category'),
          getRequest('customer'),
        ]);
        setServices(categoryResponse?.data || []);
        setCustomers(customerResponse?.data || []);
      } catch (error) {
        toast.error('Unable to load order form data. Please refresh and try again.');
      }
    };
    loadFormData();
  }, []);

  const totalAmount = useMemo(
    () => items.reduce((total, item) => total + Number(item.price || 0) * Number(item.quantity || 0), 0),
    [items]
  );

  const updateOrder = (event) => {
    const { name, value } = event.target;
    setOrder((previous) => ({ ...previous, [name]: value }));
  };

  const selectCustomer = (option) => {
    const customer = option?.data;
    if (!customer) {
      setOrder((previous) => ({ ...previous, customer_id: '' }));
      return;
    }
    setOrder((previous) => ({
      ...previous,
      customer_id: customer.id || '',
      name: customer.name || '',
      phone: customer.mobile || customer.phone || '',
      city: customer.city || previous.city,
      shipping_address: customer.address || customer.shipping_address || previous.shipping_address,
    }));
  };

  const addService = (option) => {
    const service = option?.data;
    if (!service || items.some((item) => String(item.product_id) === String(service.id))) return;
    setItems((previous) => [...previous, {
      product_id: service.id,
      name: service.name,
      price: Number(service.price || service.sprice || 0),
      quantity: 1,
    }]);
  };

  const updateItem = (index, field, value) => {
    setItems((previous) => previous.map((item, itemIndex) => (
      itemIndex === index ? { ...item, [field]: value } : item
    )));
  };

  const removeItem = (index) => setItems((previous) => previous.filter((_, itemIndex) => itemIndex !== index));

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!items.length) {
      toast.error('Add at least one service to this order.');
      return;
    }
    setSaving(true);
    try {
      const orderId = generateOrderID();
      let customerId = order.customer_id;

      // A custom order can be entered for a new customer. Create that customer
      // first so the order is always linked to a real customer record.
      if (!customerId) {
        const customerResponse = await postRequest('customer', {
          name: order.name,
          mobile: order.phone,
          address: order.shipping_address,
          city: order.city,
        });
        const createdCustomer = customerResponse?.data?.data || customerResponse?.data;
        customerId = createdCustomer?.id;

        if (!customerResponse?.status || customerResponse.status < 200 || customerResponse.status >= 300 || !customerId) {
          throw new Error(createdCustomer?.message || 'Unable to create the customer record.');
        }
      }

      const orderItems = items.map(({ product_id, quantity, price }) => ({
        product_id,
        quantity: Number(quantity),
        price: Number(price),
      }));
      const payload = {
        orderID: orderId,
        customerID: customerId,
        totalAmount,
        phoneNumber: order.phone,
        address: order.shipping_address,
        time: order.timing,
        city: order.city,
        date: order.order_date,
        message: order.message,
        alternatePhone: order.alternatePhone,
        orderType: order.orderType,
        latitude: Number(order.latitude) || 0,
        longitude: Number(order.longitude) || 0,
        items: orderItems,
        payment_method: 'cash'
      };
      const response = await postRequest('order', payload);
      if (response?.status >= 200 && response.status < 300) {
        toast.success('Custom order created successfully.');
        navigate('/customerorder');
        return;
      }
      toast.error(response?.data?.message || 'Unable to create the custom order.');
    } catch (error) {
      toast.error(error?.message || 'Unable to create the custom order.');
    } finally {
      setSaving(false);
    }
  };

  const customerOptions = customers.map((customer) => ({
    value: customer.id,
    data: customer,
    label: `${customer.name || 'Unnamed customer'}${customer.mobile ? ` — ${customer.mobile}` : ''}`,
  }));
  const serviceOptions = services.map((service) => ({
    value: service.id,
    data: service,
    label: `${service.name}${service.price || service.sprice ? ` — ₹${service.price || service.sprice}` : ''}`,
  }));
  return (
    <div className="container-fluid mt-4 px-4">
      <div className="mainheadig mb-4">
        <h4 className="text-white font-weight-bold mb-0">Create Custom Order</h4>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="card shadow-sm mb-4">
          <div className="card-header font-weight-bold">Customer details</div>
          <div className="card-body"><div className="row">
            <div className="col-md-6 mb-3"><label htmlFor="existing-customer">Existing customer (optional)</label><Select inputId="existing-customer" options={customerOptions} onChange={selectCustomer} isClearable placeholder="Search and select a customer" /></div>
            <div className="col-md-3 mb-3"><label htmlFor="customer-name">Customer name</label><input id="customer-name" className="form-control" name="name" value={order.name} onChange={updateOrder} required  disabled/></div>
            <div className="col-md-3 mb-3"><label htmlFor="customer-phone">Mobile number</label><input id="customer-phone" className="form-control" name="phone" value={order.phone} onChange={updateOrder} required  disabled/></div>
            <div className="col-md-4 mb-3"><label htmlFor="customer-city">City</label><Select inputId="customer-city" options={cityOptions} value={cityOptions.find((option) => option.value === order.city) || null} onChange={(option) => setOrder((previous) => ({ ...previous, city: option?.value || '' }))} placeholder="Search and select city" isSearchable isClearable required /></div>
            <div className="col-md-8 mb-3"><label htmlFor="service-address">Service address</label><input id="service-address" className="form-control" name="shipping_address" value={order.shipping_address} onChange={updateOrder} required /></div>
          </div></div>
        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-header font-weight-bold">Services</div>
          <div className="card-body">
            <div className="mb-3"><label htmlFor="add-service">Add service</label><Select inputId="add-service" options={serviceOptions} onChange={addService} value={null} placeholder="Search and add a service" /></div>
            <div className="table-responsive"><table className="table table-bordered mb-0">
              <thead><tr><th>Service</th><th width="150">Price</th><th width="130">Quantity</th><th width="150">Subtotal</th><th width="80"></th></tr></thead>
              <tbody>{items.length ? items.map((item, index) => <tr key={item.product_id}>
                <td>{item.name}</td><td><input min="0" step="0.01" type="number" className="form-control" value={item.price} onChange={(event) => updateItem(index, 'price', event.target.value)} /></td>
                <td><input min="1" type="number" className="form-control" value={item.quantity} onChange={(event) => updateItem(index, 'quantity', event.target.value)} /></td>
                <td>₹{(Number(item.price || 0) * Number(item.quantity || 0)).toFixed(2)}</td><td><button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeItem(index)}>Remove</button></td>
              </tr>) : <tr><td colSpan="5" className="text-center text-muted">No services added yet.</td></tr>}</tbody>
              <tfoot><tr><th colSpan="3" className="text-right">Total</th><th>₹{totalAmount.toFixed(2)}</th><th /></tr></tfoot>
            </table></div>
          </div>
        </div>

        <div className="card shadow-sm mb-4"><div className="card-header font-weight-bold">Order details</div><div className="card-body"><div className="row">
          <div className="col-md-3 mb-3"><label htmlFor="order-date">Service date</label><input id="order-date" type="date" className="form-control" name="order_date" value={order.order_date} onChange={updateOrder} required /></div>
          <div className="col-md-3 mb-3"><label htmlFor="order-time">Preferred time</label><input id="order-time" type="time" className="form-control" name="timing" value={order.timing} onChange={updateOrder} /></div>
          <div className="col-md-3 mb-3"><label htmlFor="alternate-phone">Alternate phone</label><input id="alternate-phone" className="form-control" name="alternatePhone" value={order.alternatePhone} onChange={updateOrder} /></div>
          <div className="col-md-3 mb-3"><label htmlFor="order-type">Order type</label><input id="order-type" className="form-control" name="orderType" value={order.orderType} onChange={updateOrder} required /></div>
          <div className="col-md-3 mb-3"><label htmlFor="latitude">Latitude</label><input id="latitude" type="number" step="any" className="form-control" name="latitude" value={order.latitude} onChange={updateOrder} /></div>
          <div className="col-md-3 mb-3"><label htmlFor="longitude">Longitude</label><input id="longitude" type="number" step="any" className="form-control" name="longitude" value={order.longitude} onChange={updateOrder} /></div>
          <div className="col-md-12 mb-3"><label htmlFor="order-notes">Notes</label><textarea id="order-notes" rows="2" className="form-control" name="message" value={order.message} onChange={updateOrder} placeholder="Any special instructions" /></div>
        </div></div></div>
        <div className="d-flex justify-content-end mb-5"><button type="button" className="btn btn-light mr-2" onClick={() => navigate('/customerorder')}>Cancel</button><button className="btn addbtn" disabled={saving}>{saving ? 'Creating…' : 'Create Custom Order'}</button></div>
      </form>
    </div>
  );
};

export default CreateCustomOrder;
